import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Shield, BarChart3, Target, Settings, CheckCircle2, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}
export function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);
  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
  };
  const handleRejectAll = () => {
    const onlyNecessary: CookiePreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
  };
  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
  };
  const cookieTypes = [{
    id: 'necessary' as keyof CookiePreferences,
    name: 'Cookies Necesarias',
    description: 'Esenciales para el funcionamiento básico del sitio web. No se pueden desactivar.',
    icon: Shield,
    required: true,
    examples: 'Sesión, carrito de compra, seguridad'
  }, {
    id: 'analytics' as keyof CookiePreferences,
    name: 'Cookies Analíticas',
    description: 'Nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web.',
    icon: BarChart3,
    required: false,
    examples: 'Google Analytics, estadísticas de uso'
  }, {
    id: 'marketing' as keyof CookiePreferences,
    name: 'Cookies de Marketing',
    description: 'Utilizadas para mostrar anuncios relevantes y medir la efectividad de campañas.',
    icon: Target,
    required: false,
    examples: 'Facebook Pixel, Google Ads'
  }, {
    id: 'preferences' as keyof CookiePreferences,
    name: 'Cookies de Preferencias',
    description: 'Permiten recordar tus preferencias y personalizar tu experiencia.',
    icon: Settings,
    required: false,
    examples: 'Idioma, tema, configuración'
  }];
  if (!isVisible) return null;
  return <AnimatePresence data-id="element-1314">
      <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} exit={{
      y: 100,
      opacity: 0
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 30
    }} className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6" data-id="element-1315">
        <div className="max-w-7xl mx-auto" data-id="element-1316">
          <div className="bg-white rounded-3xl shadow-soft-2xl border border-gray-100 overflow-hidden backdrop-blur-xl" data-id="element-1317">
            {!showPreferences ?
          // Simple View
          <div className="p-6 sm:p-8" data-id="element-1318">
                <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6" data-id="element-1319">
                  {/* Icon & Content */}
                  <div className="flex items-start gap-4 flex-1" data-id="element-1320">
                    <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center shrink-0" data-id="element-1321">
                      <Cookie className="w-7 h-7 text-primary-600" data-id="element-1322" />
                    </div>
                    <div className="flex-1" data-id="element-1323">
                      <h3 className="text-lg font-bold text-charcoal-900 mb-2 font-serif" data-id="element-1324">
                        Respetamos tu privacidad
                      </h3>
                      <p className="text-sm text-charcoal-600 leading-relaxed mb-3" data-id="element-1325">
                        Utilizamos cookies para mejorar tu experiencia, analizar
                        el tráfico y personalizar el contenido. Puedes aceptar
                        todas, rechazar las opcionales o configurar tus
                        preferencias.
                      </p>
                      <Link to="/cookies" className="text-sm text-primary-600 hover:text-primary-700 font-bold inline-flex items-center gap-1 transition-colors" data-id="element-1326">
                        <Info className="w-4 h-4" data-id="element-1327" />
                        Más información sobre cookies
                      </Link>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto" data-id="element-1328">
                    <Button variant="ghost" size="md" onClick={() => setShowPreferences(true)} className="rounded-xl font-bold" data-id="element-1329">
                      <Settings className="w-4 h-4 mr-2" data-id="element-1330" />
                      Configurar
                    </Button>
                    <Button variant="outline" size="md" onClick={handleRejectAll} className="rounded-xl font-bold" data-id="element-1331">
                      Rechazar opcionales
                    </Button>
                    <Button variant="primary" size="md" onClick={handleAcceptAll} className="rounded-xl font-bold shadow-soft-lg" data-id="element-1332">
                      <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-1333" />
                      Aceptar todas
                    </Button>
                  </div>
                </div>
              </div> :
          // Detailed Preferences View
          <div className="p-6 sm:p-8" data-id="element-1334">
                <div className="flex items-center justify-between mb-8" data-id="element-1335">
                  <div data-id="element-1336">
                    <h3 className="text-2xl font-bold text-charcoal-900 font-serif mb-2" data-id="element-1337">
                      Configuración de Cookies
                    </h3>
                    <p className="text-sm text-charcoal-600" data-id="element-1338">
                      Personaliza qué cookies quieres permitir
                    </p>
                  </div>
                  <button onClick={() => setShowPreferences(false)} className="w-10 h-10 rounded-xl hover:bg-gray-100 flex items-center justify-center text-charcoal-400 hover:text-charcoal-600 transition-colors" aria-label="Cerrar configuración" data-id="element-1339">
                    <X className="w-5 h-5" data-id="element-1340" />
                  </button>
                </div>

                <div className="space-y-4 mb-8" data-id="element-1341">
                  {cookieTypes.map(type => <motion.label key={type.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} className={`flex items-start gap-4 p-5 rounded-2xl border-2 transition-all duration-200 cursor-pointer ${preferences[type.id] || type.required ? 'border-primary-200 bg-primary-50/30' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`} data-id="element-1342">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${preferences[type.id] || type.required ? 'bg-primary-600 text-white' : 'bg-white text-charcoal-400'}`} data-id="element-1343">
                        <type.icon className="w-6 h-6" data-id="element-1344" />
                      </div>

                      <div className="flex-1 min-w-0" data-id="element-1345">
                        <div className="flex items-center gap-3 mb-2" data-id="element-1346">
                          <span className="text-base font-bold text-charcoal-900" data-id="element-1347">
                            {type.name}
                          </span>
                          {type.required && <Badge variant="outline" size="sm" className="font-bold" data-id="element-1348">
                              Requeridas
                            </Badge>}
                        </div>
                        <p className="text-sm text-charcoal-600 leading-relaxed mb-2" data-id="element-1349">
                          {type.description}
                        </p>
                        <p className="text-xs text-charcoal-500 font-medium" data-id="element-1350">
                          <span className="font-bold" data-id="element-1351">Ejemplos:</span>{' '}
                          {type.examples}
                        </p>
                      </div>

                      <div className="flex items-center shrink-0" data-id="element-1352">
                        <div className={`relative w-14 h-8 rounded-full transition-colors ${preferences[type.id] || type.required ? 'bg-primary-600' : 'bg-gray-300'}`} data-id="element-1353">
                          <motion.div animate={{
                      x: preferences[type.id] || type.required ? 24 : 2
                    }} transition={{
                      type: 'spring',
                      stiffness: 500,
                      damping: 30
                    }} className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-sm" data-id="element-1354" />
                        </div>
                        <input type="checkbox" checked={preferences[type.id]} onChange={e => !type.required && setPreferences({
                    ...preferences,
                    [type.id]: e.target.checked
                  })} disabled={type.required} className="sr-only" data-id="element-1355" />
                      </div>
                    </motion.label>)}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-gray-100" data-id="element-1356">
                  <p className="text-xs text-charcoal-500 text-center sm:text-left" data-id="element-1357">
                    Puedes cambiar tus preferencias en cualquier momento desde
                    la{' '}
                    <Link to="/cookies" className="text-primary-600 hover:underline font-bold" data-id="element-1358">
                      política de cookies
                    </Link>
                  </p>
                  <div className="flex gap-3" data-id="element-1359">
                    <Button variant="outline" size="md" onClick={handleRejectAll} className="rounded-xl font-bold" data-id="element-1360">
                      Rechazar opcionales
                    </Button>
                    <Button variant="primary" size="md" onClick={handleSavePreferences} className="rounded-xl font-bold shadow-soft-lg" data-id="element-1361">
                      <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-1362" />
                      Guardar preferencias
                    </Button>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>;
}